import { StandardEvents } from '@shopify/events';

const TOKEN_STORAGE_KEY = 'lonvory.staff-location.device-token.v1';
const ACTIVATION_PARAMETER = 'lonvory-staff';

function storedToken() {
  try {
    return window.localStorage.getItem(TOKEN_STORAGE_KEY)?.trim() || '';
  } catch {
    return '';
  }
}

function storeToken(token) {
  try {
    if (token) window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    else window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // The component remains hidden if browser storage is unavailable.
  }
}

function consumeActivationFragment() {
  const fragment = window.location.hash.slice(1);
  if (!fragment) return;
  const parameters = new URLSearchParams(fragment);
  const supplied = parameters.get(ACTIVATION_PARAMETER)?.trim();
  if (!supplied) return;

  if (supplied.length >= 32) {
    storeToken(supplied);
  }

  window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`);
}

function displayValue(value, fallback = '–') {
  return value === null || value === undefined ? fallback : String(value);
}

class LonvoryStaffLocation extends HTMLElement {
  #abortController;
  #eventTarget;

  connectedCallback() {
    consumeActivationFragment();
    const token = storedToken();
    if (!token) return;

    this.#renderInterface();
    this.hidden = false;
    this.querySelector('[data-staff-disable]')?.addEventListener('click', this.#disable);
    this.#eventTarget = this.closest('[id*="ProductInformation-"]');
    this.#eventTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#load(this.dataset.ean || '', token);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.querySelector('[data-staff-disable]')?.removeEventListener('click', this.#disable);
    this.#eventTarget?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  #disable = () => {
    storeToken('');
    this.#abortController?.abort();
    this.hidden = true;
    this.replaceChildren();
  };

  #renderInterface() {
    if (this.querySelector('[data-staff-status]')) return;

    const heading = document.createElement('div');
    heading.className = 'lonvory-staff-location__heading';

    const headingLabel = document.createElement('div');
    const badge = document.createElement('span');
    badge.className = 'lonvory-staff-location__badge';
    badge.textContent = 'Intern';
    const title = document.createElement('strong');
    title.textContent = 'Lagerinformation';
    headingLabel.append(badge, title);

    const disableButton = document.createElement('button');
    disableButton.type = 'button';
    disableButton.className = 'lonvory-staff-location__disable';
    disableButton.dataset.staffDisable = '';
    disableButton.textContent = 'Modus beenden';
    heading.append(headingLabel, disableButton);

    const status = document.createElement('p');
    status.className = 'lonvory-staff-location__status';
    status.dataset.staffStatus = '';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    const locations = document.createElement('div');
    locations.className = 'lonvory-staff-location__locations';
    locations.dataset.staffLocations = '';
    locations.hidden = true;

    const table = document.createElement('table');
    const tableHead = document.createElement('thead');
    const headingRow = document.createElement('tr');
    for (const label of ['Gang', 'Platz', 'Box', 'Menge']) {
      const cell = document.createElement('th');
      cell.scope = 'col';
      cell.textContent = label;
      headingRow.append(cell);
    }
    tableHead.append(headingRow);

    const tableBody = document.createElement('tbody');
    tableBody.dataset.staffLocationRows = '';
    table.append(tableHead, tableBody);
    locations.append(table);

    const ean = document.createElement('p');
    ean.className = 'lonvory-staff-location__ean';
    ean.dataset.staffEan = '';

    this.replaceChildren(heading, status, locations, ean);
  }

  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;
        if (!detail.newProduct && detail.productId && detail.productId !== this.dataset.productId) return;
        if (detail.newProduct?.id) this.dataset.productId = detail.newProduct.id;
        const renderedComponent = detail.html?.querySelector('lonvory-staff-location');
        const ean = renderedComponent?.dataset.ean || detail.resource?.barcode || '';
        this.#load(ean, storedToken());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') this.#showStatus('Lagerplatz konnte nicht aktualisiert werden.');
      });
  };

  async #load(ean, token) {
    this.#abortController?.abort();
    this.#clearLocations();
    this.#showEan(ean);

    if (!token) {
      this.hidden = true;
      return;
    }
    if (!/^\d{8,14}$/.test(ean)) {
      this.#showStatus('Für diese Variante ist keine EAN hinterlegt.');
      return;
    }
    const endpoint = this.dataset.endpoint?.trim();
    if (!endpoint) {
      this.#showStatus('Die Lagerplatzabfrage ist noch nicht konfiguriert.');
      return;
    }

    this.#showStatus('Lagerplatz wird geladen …');
    this.#abortController = new AbortController();

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({ ean }),
        signal: this.#abortController.signal,
      });

      if (response.status === 401) {
        storeToken('');
        this.#showStatus('Die Tablet-Freigabe ist abgelaufen. Bitte erneut aktivieren.');
        return;
      }
      if (response.status === 404) {
        this.#showStatus('Artikel wurde im Lagersystem nicht gefunden.');
        return;
      }
      if (!response.ok) throw new Error(`Lagerplatzabfrage fehlgeschlagen (${response.status}).`);

      const payload = await response.json();
      const locations = Array.isArray(payload.locations) ? payload.locations : [];
      if (!locations.length) {
        this.#showStatus('Für diesen Artikel ist kein Lagerplatz hinterlegt.');
        return;
      }

      this.#showStatus(locations.length === 1 ? '1 Lagerplatz gefunden' : `${locations.length} Lagerplätze gefunden`);
      this.#showLocations(locations);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.#showStatus('Lagerplatz momentan nicht erreichbar. Bitte erneut versuchen.');
    }
  }

  #showStatus(message) {
    const element = this.querySelector('[data-staff-status]');
    if (element) element.textContent = message;
  }

  #showEan(ean) {
    const element = this.querySelector('[data-staff-ean]');
    if (element) element.textContent = ean ? `EAN: ${ean}` : '';
  }

  #clearLocations() {
    const table = this.querySelector('[data-staff-locations]');
    const rows = this.querySelector('[data-staff-location-rows]');
    rows?.replaceChildren();
    if (table) table.hidden = true;
  }

  #showLocations(locations) {
    const table = this.querySelector('[data-staff-locations]');
    const rows = this.querySelector('[data-staff-location-rows]');
    if (!table || !rows) return;

    const items = locations.map((location) => {
      const row = document.createElement('tr');
      const values = [
        displayValue(location.corridor),
        displayValue(location.shelf),
        displayValue(location.box, ''),
        location.quantity === null || location.quantity === undefined ? '–' : `${location.quantity}x`,
      ];
      for (const value of values) {
        const cell = document.createElement('td');
        cell.textContent = value;
        row.append(cell);
      }
      return row;
    });

    rows.replaceChildren(...items);
    table.hidden = false;
  }
}

if (!customElements.get('lonvory-staff-location')) {
  customElements.define('lonvory-staff-location', LonvoryStaffLocation);
}
